<template>
  <div class="product-availability">
    <div class="pa-entry">
      <fn-icon
        class="pa-icon"
        icon="delivery-new"
        size="s"
      />

      <span class="pa-name">
        <strong>{{ $t('shipping.modes.delivery') }}</strong>
      </span>

      <div class="pa-details">
        <fn-product-shipping-duration
          :shipping-information="product.shippingInformation"
          :stock="product.stock"
          :marketplace-article="product.marketplaceArticle"
          :marketplace-partner-name="product.marketplacePartnerName"
          is-pdp
          @open-marketplace-modal="openMarketplaceModal = true"
        />
      </div>
    </div>

    <div
      v-if="showPosAvailability"
      class="pa-entry"
    >
      <fn-icon
        class="pa-icon"
        icon="store-new"
        size="xs"
      />
      <span class="pa-name">
        <strong>{{ storeHeadline }}</strong>
      </span>

      <div class="pa-details pa-store-check">
        <span v-if="!Object.keys(selectedStore).length && !isMarketplaceArticle">
          {{ $t('shipping.selectStore') }}
        </span>

        <fn-button
          v-if="!hasAvailabilityChecked && Object.keys(selectedStore).length && !isMarketplaceArticle"
          class="pad-button"
          tertiary-alt
          @click="handleAvailabilityCheck"
        >
          {{ $t('cart.deliveryMethods.checkStoreAvailability') }}
        </fn-button>

        <fn-product-availability-store-check
          v-if="(clickAndCollectEnabled && hasAvailabilityChecked) || isMarketplaceArticle"
          :is-click-and-collect-eligible="isClickAndCollectEligible"
        />

        <template v-if="selectedStore && Object.keys(storeStock).length && !isMarketplaceArticle">
          <fn-product-shipping-duration
            v-if="storeStock && !isMarketplaceArticle"
            class="pad-shipping"
            :click-and-collect="selectedStore?.clickAndCollectFeaturesInStore"
            :store-availability="storeStock"
            :shipping-information="product.shippingInformation"
            :stock="storeStock"
            is-pdp
          />
        </template>

        <div
          v-if="!isMarketplaceArticle"
          :class="[
            'pa-store-button',
            {
              'pa-store-button--no-padding': !clickAndCollectEnabled,
            },
          ]"
        >
          <fn-button
            plain
            underline
            @click="handleSubmit"
          >
            {{ submitButtonText }}
          </fn-button>
        </div>
      </div>
    </div>
  </div>

  <div class="pa-usps">
    <fn-list
      unstyled
      icons
      spaced
    >
      <li v-if="isClickAndCollectEligible">
        <fn-icon
          icon="check"
          size="xs"
          role="presentation"
        />
        <span> {{ $t('shipping.clickAndCollectUsp') }}</span>
      </li>
      <li>
        <fn-icon
          icon="check"
          size="xs"
          role="presentation"
        />
        <span>
          {{ $t('shipping.freeShippingText') }}
          <template v-if="product.shippingInformation.shippingFee">
            {{
              $t('shipping.freeShippingTreshholdText', {
                freeShipping: product.shippingInformation.freeShippingThreshold.formattedValue,
              })
            }}
          </template></span
        >
      </li>
      <li>
        <fn-icon
          icon="check"
          size="xs"
          role="presentation"
        />
        <span> {{ $t('shipping.freeReturns') }}</span>
      </li>
    </fn-list>
  </div>

  <fn-modal-product-info
    v-if="openMarketplaceModal"
    :marketplace-modal="openMarketplaceModal"
    @close="openMarketplaceModal = false"
  />

  <fn-modal-service-locations
    v-if="isModalVisible"
    :headline="$t('product.availability.title')"
    :product="product"
    :search-payload-initial="searchPayload"
    :product-id="product.code"
    @close="displaySelectedStore"
    @select-store="selectStore"
  />
</template>

<script>
import { mapState } from 'pinia';
import { inStock } from '@/utils/stock';

export default {
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isModalVisible: false,
    openMarketplaceModal: false,
    storeStock: {},
    selectedStore: {},
    searchPayload: '',
    hasAvailabilityChecked: false,
  }),
  computed: {
    ...mapState(useUserStore, {
      isAuthenticated: 'getIsAuthenticated',
      getUserFavoriteStore: 'getUserFavoriteStore',
    }),

    // we have to use this endpoint because `/stores/${storeId}/availability/${productId}`
    // is not providing the correct data / enough data for us
    ...mapState(useMainStore, {
      getStoreListSorted: 'getStoreListSorted',
      getProductAvailabilityInStore: 'getProductAvailabilityInStore',
      clickAndCollectEnabled: 'getClickAndCollectEnabled',
      showPosAvailability: 'getShowPosAvailability',
    }),

    isClickAndCollectEligible() {
      return this.product.availableForClickAndCollect && inStock(this.product.stock.stockLevelStatus);
    },

    isMarketplaceArticle() {
      return this.product.marketplaceArticle;
    },

    favoriteStore() {
      return this.isAuthenticated ? this.getUserFavoriteStore : getFromLocalStorage('favoriteStore');
    },

    selectStoreFromStorage() {
      const sessionStore = sessionStorage.getItem('selectedStore');
      return JSON.parse(sessionStore);
    },

    storeHeadline() {
      return this.selectedStore?.displayName ?? this.$t('cart.deliveryMethods.pdpPickup');
    },

    submitButtonText() {
      if (!Object.keys(this.selectedStore).length) {
        return this.$t('cart.deliveryMethods.selectStoreButton');
      }
      return this.$t('cart.deliveryMethods.changeStoreButton');
    },
  },
  mounted() {
    if (this.favoriteStore && !this.selectStoreFromStorage.value) {
      this.selectedStore = this.favoriteStore;
    }

    if (this.selectStoreFromStorage) {
      this.selectedStore = this.selectStoreFromStorage;
    }

    this.searchPayload = this.selectedStore?.address?.postalCode;
  },

  methods: {
    ...mapActions(useMainStore, {
      startLoading: 'startLoading',
      finishLoading: 'finishLoading',
    }),

    handleSubmit() {
      this.isModalVisible = true;
      this.searchPayload = this.selectedStore?.address?.postalCode;
    },

    displaySelectedStore() {
      this.isModalVisible = false;
    },

    handleAvailabilityCheck() {
      this.startLoading();

      const searchPayload = {
        productId: this.product.code,
        storeId: [this.selectedStore.name],
      };

      this.getProductAvailabilityInStore(searchPayload)
        .then((response) => {
          this.hasAvailabilityChecked = true;
          this.storeStock = response?.[0] ?? {};
        })
        .catch(() => {
          this.error = this.$t('validation.general');
          this.hasAvailabilityChecked = false;
        })
        .finally(() => {
          this.finishLoading();
        });
    },

    selectStore(store, searchPayload) {
      this.hasAvailabilityChecked = true;
      this.selectedStore = store;
      this.storeStock = store.storeStock;
      this.searchPayload = searchPayload.query;
      this.isModalVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-availability {
  padding: $pad;
  gap: $pad;
  margin-top: $pad;
  background: $color-general-background-neutral-default;

  @include grid-area-classes(
    (
      'pa-icon': 'icon',
      'pa-name': 'name',
      'pa-details': 'details',
      'pa-store-button': 'store-button',
      'pa-availability': 'availability',
    )
  );

  .pa-entry {
    grid-template-columns: 25px [last];
    grid-template-areas:
      'icon name'
      '. details'
      '. store-button';
    display: grid;
    align-items: center;
    gap: math.div($pad, 2);
    border-bottom: map-get($border-width, xs) solid $color-action-border-neutral-low-base;

    &:last-child {
      padding-top: $pad;
      padding-bottom: math.div($pad, 4);
      grid-template-areas:
        'icon name'
        '. details'
        '. store-button';
      display: grid;
    }

    .pa-details,
    .pa-availability {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: math.div($pad, 1.6);
      margin-bottom: math.div($pad, 2);
    }

    .pad-button {
      margin-top: $pad;
    }

    .pad-shipping {
      margin-top: 10px;
    }

    .pa-store-check {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
      margin-bottom: 0;
    }

    .pa-store-button {
      padding-top: $pad;

      &.pa-store-button--no-padding {
        padding-top: 0;
      }
    }
  }
}

.pa-usps {
  margin-top: $pad;
  padding: 0 $pad;

  ul {
    border-bottom: map-get($border-width, xs) solid $color-action-border-neutral-low-base;
    padding-bottom: 32px;
  }

  .icon {
    color: $color-general-icons-neutral-higher;
  }
}
</style>
